// =============================================================================
// FILE PATH: src/modules/pms/components/WBSTree.js
// =============================================================================

import React, { useEffect, useState } from "react";
import WBSNodeRow from "./WBSNodeRow";

export default function WBSTree({
  data = [],
  onAdd,
  onDependency,
  onEdit,
  onDelete,
  onSelect,
  selectedNode,
  predecessorMap = {},
  successorMap = {}
}) {

  // ===========================================================================
  // EXPAND / COLLAPSE STATE
  // ===========================================================================
  const [expandedNodes, setExpandedNodes] = useState({});

  // ---------------------------------------------------------
  // AUTO EXPAND ROOT NODES
  // ---------------------------------------------------------
  useEffect(() => {

    const initialExpanded = {};

    const expandAll = (nodes = []) => {

      nodes.forEach((node) => {

        initialExpanded[node.id] = true;

        if (node.children && node.children.length > 0) {
          expandAll(node.children);
        }
      });
    };

    expandAll(data);

    setExpandedNodes(initialExpanded);

  }, [data]);

  // ---------------------------------------------------------
  // TOGGLE NODE
  // ---------------------------------------------------------
  const toggleExpand = (nodeId) => {

    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  // ---------------------------------------------------------
  // VALIDATE CHILD CREATION
  // ---------------------------------------------------------
  const canAddChild = (nodeType) => {

    switch (nodeType) {

      case "ITEM":
        return true;

      case "PHASE":
        return true;

      case "TASK":
      default:
        return false;
    }
  };

  // ---------------------------------------------------------
  // RENDER NODES WITH WBS NUMBERING
  // ---------------------------------------------------------
  const renderNodes = (
    nodes,
    level = 0,
    parentCode = ""
  ) => {

    return nodes.map((node, index) => {

      // -----------------------------------------------------
      // GENERATE WBS CODE
      // -----------------------------------------------------
      const wbsCode = parentCode
        ? `${parentCode}.${index + 1}`
        : `${index + 1}`;

      // -----------------------------------------------------
      // EXPAND STATE
      // -----------------------------------------------------
      const isExpanded = expandedNodes[node.id];

      const hasChildren =
        node.children &&
        node.children.length > 0;

      return (
        <React.Fragment key={node.id}>

          <WBSNodeRow
            node={node}
            level={level}

            // 🔥 NEW
            wbsCode={wbsCode}

            // 🔥 EXPAND SUPPORT
            isExpanded={isExpanded}
            hasChildren={hasChildren}
            onToggleExpand={toggleExpand}

            onAdd={onAdd}
            onEdit={onEdit}
            onDelete={onDelete}
            onDependency={onDependency}

            onSelect={onSelect}
            selectedNode={selectedNode}

            predecessorMap={predecessorMap}
            successorMap={successorMap}

            canAddChild={canAddChild(node.type)}
          />

          {/* ------------------------------------------------- */}
          {/* CHILDREN */}
          {/* ------------------------------------------------- */}
          {hasChildren && isExpanded &&
            renderNodes(
              node.children,
              level + 1,
              wbsCode
            )}

        </React.Fragment>
      );
    });
  };

  // ---------------------------------------------------------
  // EMPTY STATE
  // ---------------------------------------------------------
  if (!data || data.length === 0) {

    return (
      <div style={styles.emptyContainer}>

        {/* HEADER */}
        <div style={styles.headerRow}>
          <div style={styles.colWbs}>WBS</div>
          <div style={styles.colId}>WBS ID</div>
          <div style={styles.colName}>Name</div>
          <div style={styles.colType}>Type</div>
          <div style={styles.colAssigned}>Assigned</div>
          <div style={styles.colStart}>Start</div>
          <div style={styles.colEnd}>End</div>

          <div style={styles.colPredecessor}>Predecessor</div>
          <div style={styles.colSuccessor}>Successor</div>

          <div style={styles.colStatus}>Status</div>
          <div style={styles.colActions}>Actions</div>
        </div>

        <div style={styles.emptyMessage}>
          No WBS nodes yet. Click <b>+ Add Root</b> to start.
        </div>

      </div>
    );
  }

  // ---------------------------------------------------------
  // MAIN RENDER
  // ---------------------------------------------------------
  return (
    <div style={styles.container}>

      {/* HEADER */}
      <div style={styles.headerRow}>
        <div style={styles.colWbs}>WBS</div>
        <div style={styles.colId}>WBS ID</div>
        <div style={styles.colName}>Name</div>
        <div style={styles.colType}>Type</div>
        <div style={styles.colAssigned}>Assigned</div>
        <div style={styles.colStart}>Start</div>
        <div style={styles.colEnd}>End</div>

        <div style={styles.colPredecessor}>Predecessor</div>
        <div style={styles.colSuccessor}>Successor</div>

        <div style={styles.colStatus}>Status</div>
        <div style={styles.colActions}>Actions</div>
      </div>

      <div style={styles.treeBody}>
        {renderNodes(data)}
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================
const styles = {

  container: {
    marginTop: 20,
    border: "1px solid #ddd",
    borderRadius: 6,
    background: "#fff",

    display: "flex",
    flexDirection: "column",

    height: "75vh"
  },

  headerRow: {
    display: "flex",
    position: "sticky",
    top: 0,
    zIndex: 10,
    background: "#f3f4f6",
    padding: "10px 12px",
    fontWeight: 600,
    borderBottom: "1px solid #ddd",
    alignItems: "center"
  },

  colWbs: {
    flex: 1,
    textAlign: "center"
  },

  colId: {
    flex: 1,
    textAlign: "center"
  },

  colName: {
    flex: 3
  },

  colType: {
    flex: 1,
    textAlign: "center"
  },

  colAssigned: {
    flex: 2,
    textAlign: "center"
  },

  colStart: {
    flex: 2,
    textAlign: "center"
  },

  colEnd: {
    flex: 2,
    textAlign: "center"
  },

  colPredecessor: {
    flex: 2,
    textAlign: "center"
  },

  colSuccessor: {
    flex: 2,
    textAlign: "center"
  },

  colStatus: {
    flex: 2,
    textAlign: "center"
  },

  colActions: {
    flex: 1,
    textAlign: "center"
  },
  treeBody: {
  overflowY: "auto",
  overflowX: "hidden",
  flex: 1
},
  emptyContainer: {
    marginTop: 20,
    border: "1px solid #ddd",
    borderRadius: 6,
    overflow: "hidden",
    background: "#fff"
  },

  emptyMessage: {
    padding: 20,
    textAlign: "center",
    color: "#6b7280",
    fontSize: 14
  }
};